import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import {
  applyProductChange,
  getProducts,
  ProductChangeKind,
  ProductQuote,
  ProductsApiError,
  ProductsState,
  quoteProductChange,
} from '../../api/products-client';
import { useStaysProduct } from '../../utils/app-features';
import { useAuth } from '../../context/auth-provider';
import { can, PERMISSIONS } from '../../auth/permissions';
import { palette, spacing, typography } from '../../style-utils/theme';
import { AppButton } from '../button/app-button';
import { ConfirmDialog } from '../modal/confirm-dialog';
import { Field, FieldError, FieldHint, Input, Label } from '../form-kit';
import { formatDate } from '../../function-utils/locale';
import toast from '../../utils/toast';
import {
  CardMeta,
  CardTitle,
  KindCard,
  KindGroup,
  KindHint,
  KindTitle,
  PropertyCard,
} from '../pages/stays/properties/properties.styled';

/**
 * WHAT YOUR ACCOUNT HOLDS (Independent Stays IS6). Lobbero Desk and Lobbero Stays,
 * each with the one change it can make, on both Billing pages.
 *
 * The owner sees the price and what will happen BEFORE anything changes: the
 * server quotes, this page shows that quote, and "Confirm" sends back the amount
 * it showed, so a price that moved comes back as a new quote (QUOTE_CHANGED)
 * rather than a surprise. Every amount is the server's integer cents, and every
 * refusal is the server's own sentence.
 *
 * Hidden entirely while Stays billing is off or the read fails: the rest of the
 * billing page must never depend on this card.
 */

const Wrap = styled.section`
  margin: 0 0 ${spacing.lg};
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs} ${spacing.md};
  padding: ${spacing.xs} 0;
  min-width: 0;
`;

const RowText = styled.div`
  min-width: 0;
  flex: 1 1 240px;
`;

const Lines = styled.dl`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${spacing.half} ${spacing.md};
  margin: 0 0 ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};

  dt {
    min-width: 0;
    font-weight: ${typography.weight.medium};
  }

  dd {
    margin: 0;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
`;

const LineDetail = styled.span`
  display: block;
  font-weight: ${typography.weight.regular};
  color: ${palette.slate};
`;

const Total = styled.dt`
  font-weight: ${typography.weight.bold} !important;
`;

const TotalValue = styled.dd`
  font-weight: ${typography.weight.bold};
`;

const Effects = styled.ul`
  margin: 0 0 ${spacing.sm};
  padding-left: ${spacing.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

const Small = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

const money = (cents: number, currency = 'usd'): string =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const TITLES: Record<ProductChangeKind, string> = {
  add_desk: 'Add Lobbero Desk',
  add_stays: 'Add Lobbero Stays',
  drop_desk: 'Remove Lobbero Desk',
  drop_stays: 'Remove Lobbero Stays',
};

const DONE: Record<ProductChangeKind, string> = {
  add_desk: 'Lobbero Desk is now in your plan.',
  add_stays: 'Lobbero Stays is now in your plan.',
  drop_desk: 'Lobbero Desk was removed from your plan.',
  drop_stays: 'Lobbero Stays was removed from your plan.',
};

// A row with nothing to do says nothing: these refusals only restate the row.
const QUIET_REFUSALS = ['PRODUCTS_NEED_ONE', 'ALREADY_IN_PLAN', 'NOT_IN_PLAN'];

type Dialog = {
  kind: ProductChangeKind;
  step: 'choose' | 'quote';
  plan: string;
  slots: string;
  quote: ProductQuote | null;
};

const textOf = (e: unknown, fallback: string) => (e instanceof ProductsApiError ? e.message : fallback);

export const ProductsCard: React.FC<{ onChanged?: () => void }> = ({ onChanged }) => {
  const { permissions, refreshAuth } = useAuth();
  const canManage = can(permissions, PERMISSIONS.BILLING_MANAGE);
  const [data, setData] = useState<ProductsState | null>(null);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<ProductChangeKind | null>(null);

  // IS8.0 (IS8-D4): adding or dropping a product exists only with Lobbero Stays;
  // with Stays switched off on the server there is no card and no request.
  const staysOn = useStaysProduct() === true;
  const canRead = can(permissions, PERMISSIONS.BILLING_READ) && staysOn;
  const load = useCallback(() => {
    if (!canRead) return;
    getProducts()
      .then(setData)
      .catch(() => setData(null));
  }, [canRead]);
  useEffect(() => load(), [load]);

  if (!data || data.enabled !== true || !data.products) return null;
  // A complimentary account with nothing it can change here: Lobbero support makes
  // its changes, so the card would be a refusal and nothing else.
  if (data.billing === 'comped' && !Object.values(data.can).some(Boolean)) return null;

  const priced = async (d: Dialog) => {
    setError('');
    const q = await quoteProductChange({
      kind: d.kind,
      ...(d.kind === 'add_stays' ? { listing_slots: Number(d.slots) } : {}),
      ...(d.kind === 'drop_desk' && d.plan ? { stays_plan_code: d.plan } : {}),
      ...(d.kind === 'drop_stays' && d.plan ? { plan_code: d.plan } : {}),
    });
    setDialog({ ...d, step: 'quote', quote: q });
  };

  const choosesFirst = (kind: ProductChangeKind) =>
    kind === 'add_stays' ||
    (kind === 'drop_desk' && data.billing !== 'none') ||
    (kind === 'drop_stays' && data.account === 'bundle');

  const open = async (kind: ProductChangeKind) => {
    setError('');
    const d: Dialog = {
      kind,
      step: 'choose',
      plan: '',
      slots: String(Math.max(1, data.options.slots_in_use)),
      quote: null,
    };
    if (choosesFirst(kind)) {
      setDialog(d);

      return;
    }
    setBusy(kind);
    try {
      await priced(d);
    } catch (e) {
      setError(textOf(e, 'Could not price that change. Please try again.'));
    } finally {
      setBusy(null);
    }
  };

  const seePrice = async () => {
    if (!dialog) return;
    if (dialog.kind !== 'add_stays' && !dialog.plan) {
      setError('Choose a plan.');

      return;
    }
    try {
      await priced(dialog);
    } catch (e) {
      setError(textOf(e, 'Could not price that change. Please try again.'));
    }
  };

  const confirm = async () => {
    if (!dialog || !dialog.quote) return;
    setError('');
    try {
      const next = await applyProductChange(dialog.quote.request_id, dialog.quote.today_cents);
      const { kind } = dialog;
      setDialog(null);
      setData(next);
      toast.success(DONE[kind]);
      await refreshAuth();
      if (onChanged) onChanged();
    } catch (e) {
      // The price moved: the new quote, and the question again.
      if (e instanceof ProductsApiError && e.quote) setDialog({ ...dialog, quote: e.quote });
      setError(textOf(e, 'That did not go through, and nothing was changed.'));
      if (e instanceof ProductsApiError && e.code === 'QUOTE_EXPIRED') setDialog({ ...dialog, step: 'choose', quote: null });
      load();
    }
  };

  const rows: { name: string; held: boolean; kind: ProductChangeKind; hint: string }[] = [
    {
      name: 'Lobbero Desk',
      held: data.products.desk,
      kind: data.products.desk ? 'drop_desk' : 'add_desk',
      hint: 'The front desk: buildings, visitors, parcels, shifts and the resident portal.',
    },
    {
      name: 'Lobbero Stays',
      held: data.products.stays,
      kind: data.products.stays ? 'drop_stays' : 'add_stays',
      hint: 'Short stays: listings, reservations and online guest check-in.',
    },
  ];

  const c = data.consequences.drop_desk;
  const quote = dialog && dialog.quote;

  return (
    <Wrap data-products-card="">
      <PropertyCard as="div">
        <CardTitle>Your products</CardTitle>
        {rows.map(r => {
          const refusal = data.reason[r.kind];
          const blocker = r.kind === 'drop_stays' ? data.blockers.drop_stays[0] : null;

          return (
            <Row key={r.name} data-product-row={r.kind}>
              <RowText>
                <CardMeta>
                  <b>{r.name}</b> · {r.held ? 'In your plan' : 'Not in your plan'}
                </CardMeta>
                <Small>{r.hint}</Small>
                {refusal && !QUIET_REFUSALS.includes(refusal.code) && canManage ? (
                  <Small data-product-refusal={refusal.code}>
                    {refusal.message}
                    {blocker ? (
                      <>
                        {' '}
                        <Link to={blocker.link}>See your stays</Link>
                      </>
                    ) : null}
                  </Small>
                ) : null}
              </RowText>
              {canManage && data.can[r.kind] ? (
                <AppButton
                  variant={r.held ? 'ghost' : 'secondary'}
                  loading={busy === r.kind}
                  disabled={!!busy}
                  data-product-change={r.kind}
                  onClick={() => open(r.kind)}
                >
                  {r.held ? `Remove ${r.name}` : `Add ${r.name}`}
                </AppButton>
              ) : null}
            </Row>
          );
        })}
        {!dialog ? <FieldError message={error} /> : null}
      </PropertyCard>

      <ConfirmDialog
        open={!!dialog && dialog.step === 'choose'}
        title={dialog ? TITLES[dialog.kind] : ''}
        message={
          dialog && dialog.kind === 'add_stays'
            ? 'How many listings do you want to run? Each one holds a slot.'
            : dialog && dialog.kind === 'drop_desk'
              ? 'Which Stays plan should your account move to?'
              : 'Which Desk plan should your account move to?'
        }
        primaryLabel="See the price"
        onPrimary={seePrice}
        dismissLabel="Back"
        onDismiss={() => setDialog(null)}
      >
        {dialog && dialog.kind === 'add_stays' ? (
          <Field>
            <Label htmlFor="products-slots">Listing slots</Label>
            <Input
              id="products-slots"
              type="number"
              inputMode="numeric"
              min={Math.max(1, data.options.slots_in_use)}
              value={dialog.slots}
              onChange={e => setDialog({ ...dialog, slots: e.target.value })}
            />
            {data.options.addon_cents !== null ? (
              <FieldHint>{money(data.options.addon_cents, data.currency)} per slot / month.</FieldHint>
            ) : null}
          </Field>
        ) : dialog ? (
          <KindGroup role="radiogroup" aria-label="Plan">
            {(dialog.kind === 'drop_desk' ? data.options.stays_plans : data.options.desk_plans).map(p => (
              <KindCard
                key={p.code}
                type="button"
                role="radio"
                aria-checked={dialog.plan === p.code}
                $checked={dialog.plan === p.code}
                data-products-plan={p.code}
                onClick={() => setDialog({ ...dialog, plan: p.code })}
              >
                <KindTitle>{p.name}</KindTitle>
                <KindHint>
                  {p.code === 'stays_checkin' && data.options.checkin_cents !== null
                    ? `${money(data.options.checkin_cents, data.currency)} per completed check-in`
                    : dialog.kind === 'drop_desk'
                      ? `${money(p.unit_cents, data.currency)} per place to stay / month`
                      : `${money(p.unit_cents, data.currency)} per unit / month`}
                </KindHint>
              </KindCard>
            ))}
          </KindGroup>
        ) : null}
        {dialog ? <FieldError message={error} /> : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={!!dialog && dialog.step === 'quote' && !!quote}
        title={dialog ? TITLES[dialog.kind] : ''}
        message="Check this first. Nothing has changed yet."
        primaryLabel="Confirm"
        primaryVariant={dialog && dialog.kind.startsWith('drop') ? 'destructive' : 'primary'}
        onPrimary={confirm}
        dismissLabel="Back"
        onDismiss={() => setDialog(null)}
      >
        {dialog && quote ? (
          <div data-products-quote={dialog.kind}>
            <Lines>
              {quote.lines.map(l => (
                <React.Fragment key={l.label}>
                  <dt>
                    {l.label}
                    <LineDetail>{l.detail}</LineDetail>
                  </dt>
                  <dd>{l.cents === null ? '' : money(l.cents, quote.currency)}</dd>
                </React.Fragment>
              ))}
              <Total>Due today</Total>
              <TotalValue data-products-today="">{money(quote.today_cents, quote.currency)}</TotalValue>
              <dt>
                Next invoice
                {quote.next_invoice_at ? (
                  <LineDetail>
                    {formatDate(quote.next_invoice_at, { day: 'numeric', month: 'long', year: 'numeric' })}
                  </LineDetail>
                ) : null}
              </dt>
              <dd data-products-next="">{money(quote.next_invoice_cents, quote.currency)}</dd>
            </Lines>
            {dialog.kind === 'drop_desk' ? (
              <>
                <Effects data-products-effects="">
                  {c.concierges > 0 ? (
                    <li>
                      {c.concierges_keep_reception
                        ? `${plural(c.concierges, 'concierge keeps', 'concierges keep')} Reception only.`
                        : `${plural(c.concierges, 'concierge loses', 'concierges lose')} access at once.`}
                    </li>
                  ) : null}
                  {c.residents > 0 ? (
                    <li>
                      {plural(c.residents, 'resident', 'residents')} can no longer open the portal. Lobbero does not
                      email them, so you may want to tell them.
                    </li>
                  ) : null}
                  {c.live_passes > 0 ? <li>{plural(c.live_passes, 'visitor pass stops', 'visitor passes stop')} working.</li> : null}
                  {c.open_shifts > 0 ? <li>{plural(c.open_shifts, 'open shift is', 'open shifts are')} closed.</li> : null}
                  {c.units_hidden > 0 ? (
                    <li>
                      {plural(c.units_hidden, 'unit', 'units')} with no listing {c.units_hidden === 1 ? 'is' : 'are'} hidden
                      and no longer billed. {c.units_hidden === 1 ? 'It comes' : 'They come'} back if you add Desk again.
                    </li>
                  ) : null}
                  <li>
                    {c.listings_kept > 0
                      ? `Your ${plural(c.listings_kept, 'listing keeps', 'listings keep')} running.`
                      : 'Nothing is deleted.'}
                  </li>
                </Effects>
                <Small>
                  {quote.credit_cents > 0
                    ? `${money(quote.credit_cents, quote.currency)} of what you have paid becomes credit on your account. It pays your next invoices and is not paid out in cash. `
                    : ''}
                  <Link to="/legal/terms#fees" target="_blank" rel="noopener noreferrer">
                    How credit works
                  </Link>
                </Small>
              </>
            ) : null}
            {dialog.kind === 'drop_stays' ? (
              <Effects data-products-effects="">
                {data.consequences.drop_stays.listings_retired > 0 ? (
                  <li>{plural(data.consequences.drop_stays.listings_retired, 'listing is', 'listings are')} closed for good.</li>
                ) : null}
                <li>The Stays section disappears from your account.</li>
              </Effects>
            ) : null}
            <FieldError message={error} />
          </div>
        ) : null}
      </ConfirmDialog>
    </Wrap>
  );
};
